// React
import React, { useState, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'

// Libraries
import cuid from 'cuid'
import toast from 'react-hot-toast'

// Components
import BSwitch from 'ui/blocks/Switch'

// Shared
import { useTranslation } from 'shared/utils/useTranslation'

/**
 * Switch, like a checkbox, but fancier.
 *
 * @example
 *   <Switch checked onChange={this.handleChange} />
 */

export const Switch = ({
  id,
  disabled = false,
  field,
  label,
  description,
  inverted = false,
  withActiveEffect = true,
  block = false,
  relative = false,
  mirror = false,
  ...otherProps
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const previousValueRef = useRef(null)
  const { t } = useTranslation()

  const { onChange: fieldOnChange, value } = field

  const handleChange = useCallback(async () => {
    // Prevent double-clicking during async operation
    if (isLoading) {
      return
    }

    const newValue = !value

    setIsLoading(true)
    previousValueRef.current = value

    try {
      const result = fieldOnChange?.(newValue)

      if (result && typeof result.then === 'function') {
        await result
      }

      // If we get here, the API call was successful
      setIsLoading(false)
      previousValueRef.current = null
    } catch (error) {
      console.error('Switch change failed:', error)
      toast.error(t('common.errors.something_went_wrong'))
      // API call failed, revert to previous value
      fieldOnChange?.(previousValueRef.current)
      setIsLoading(false)
      previousValueRef.current = null
    }
  }, [fieldOnChange, value, isLoading, t])

  const isDisabled = disabled || isLoading
  const switchId = id || cuid()

  const checked = inverted ? !value : value

  return (
    <BSwitch {...otherProps}>
      <BSwitch.Input id={switchId} type="checkbox" checked={checked} onChange={handleChange} readOnly={isDisabled} />
      <BSwitch.Toggle
        data-tid={`Switch ${label}`}
        className="switch"
        htmlFor={switchId}
        checked={checked}
        disabled={isDisabled}
        readOnly={isDisabled}
        withActiveEffect={withActiveEffect}
        block={block}
        relative={relative}
        mirror={mirror}
      />
      {label && <BSwitch.Label onClick={handleChange}>{label}</BSwitch.Label>}
      {description && <BSwitch.Description>{description}</BSwitch.Description>}
    </BSwitch>
  )
}

Switch.displayName = 'Switch'

Switch.propTypes = {
  id: PropTypes.string,
  disabled: PropTypes.bool,
  field: PropTypes.shape({
    value: PropTypes.bool,
    onChange: PropTypes.func
  }).isRequired,
  label: PropTypes.any,
  description: PropTypes.any,
  inverted: PropTypes.bool,
  withActiveEffect: PropTypes.bool,
  block: PropTypes.bool,
  relative: PropTypes.bool,
  mirror: PropTypes.bool
}
